import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Business, BusinessBranding, Appointment } from '../types';
import { getAppointmentStartIso } from '../utils/appointmentHelpers';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { Clock, Calendar, User, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

type AppointmentFilter = 'all' | 'upcoming' | 'completed' | 'cancelled';

const STORAGE_KEY = 'businessAppointmentsFilters';

interface StoredFilters {
  filter: AppointmentFilter;
  page: number;
}

const BusinessAppointmentsPage: React.FC = () => {
  const { businessShortName } = useParams<{ businessShortName: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Load filters from localStorage
  const loadStoredFilters = (): StoredFilters => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          filter: parsed.filter && ['all', 'upcoming', 'completed', 'cancelled'].includes(parsed.filter) 
            ? parsed.filter 
            : 'all',
          page: parsed.page && typeof parsed.page === 'number' && parsed.page >= 1 ? parsed.page : 1
        };
      }
    } catch (error) {
      console.error('Error loading stored filters:', error);
    }
    return { filter: 'all', page: 1 };
  };

  // Save filters to localStorage
  const saveFilters = (filter: AppointmentFilter, page: number) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ filter, page }));
    } catch (error) {
      console.error('Error saving filters:', error);
    }
  };

  const initialFilters = loadStoredFilters();
  
  const [business, setBusiness] = useState<Business | null>(null);
  const [branding, setBranding] = useState<BusinessBranding | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appointmentFilter, setAppointmentFilter] = useState<AppointmentFilter>(initialFilters.filter);
  const [currentPage, setCurrentPage] = useState(initialFilters.page);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  useEffect(() => {
    const fetchBusinessData = async () => {
      if (!businessShortName || !user) return;
      
      try {
        setLoading(true);
        // Customer-facing: exclude expired businesses.
        const businessData = await apiService.getBusiness(businessShortName, { includeExpired: false });
        setBusiness(businessData.business);
        
        // Fetch branding data
        try {
          const brandingData = await apiService.getBusinessBranding(businessShortName);
          setBranding(brandingData);
        } catch (brandingError) {
          setBranding({
            logoUrl: undefined,
            primaryColor: '#e11d48',
            secondaryColor: '#9f1239',
            accentColor: '#f5c2cc',
            textColor: '#111827',
            backgroundColor: '#ffeef0',
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            useCustomColors: false,
            layout: 'modern',
            galleryImages: []
          });
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load business information');
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [businessShortName, user]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    saveFilters(appointmentFilter, currentPage);
  }, [appointmentFilter, currentPage]);

  useEffect(() => {
    const loadAppointments = async () => {
      if (!business?.id || !user) return;
      
      try {
        setLoading(true);
        const filters: any = {
          businessId: business.id,
          customerId: 'me',
          page: currentPage,
          limit: 10
        };
        
        if (appointmentFilter === 'upcoming') {
          filters.status = 'upcoming';
        } else if (appointmentFilter === 'completed') {
          filters.status = 'COMPLETED';
        } else if (appointmentFilter === 'cancelled') {
          filters.status = 'CANCELLED';
        }
        
        const response = await apiService.getCustomerAppointments(filters);
        setAppointments(response.data || []);
        
        if (response.pagination) {
          setPagination({
            currentPage: response.pagination.page || currentPage,
            totalPages: response.pagination.totalPages || 1,
            total: response.pagination.total || 0,
            hasNextPage: response.pagination.hasNextPage || false,
            hasPrevPage: response.pagination.hasPrevPage || false
          });
        }
      } catch (error) {
        console.error('Error loading appointments:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAppointments();
  }, [business?.id, user, appointmentFilter, currentPage]);

  // Extract colors from branding
  const brandColor = branding?.primaryColor || '#e11d48';
  const brandDark = branding?.secondaryColor || '#9f1239';
  const brandSoft = branding?.accentColor ? `${branding.accentColor}15` : '#fff1f2';
  const bgColor = branding?.backgroundColor || '#ffeef0';
  const surfaceColor = '#ffffff';
  const borderColor = branding?.accentColor ? `${branding.accentColor}40` : '#f5c2cc';
  const textColor = branding?.textColor || '#111827';
  const mutedColor = branding?.textColor ? `${branding.textColor}80` : '#6b7280';

  // Get business initials for logo
  const getBusinessInitials = () => {
    if (!business) return 'BS';
    const words = business.name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return business.name.substring(0, 2).toUpperCase();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' };
      case 'PENDING':
        return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' };
      case 'COMPLETED':
        return { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' };
      case 'CANCELLED':
        return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' };
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return <CheckCircle className="w-5 h-5" />;
      case 'PENDING':
        return <AlertCircle className="w-5 h-5" />;
      case 'COMPLETED':
        return <CheckCircle className="w-5 h-5" />;
      case 'CANCELLED':
        return <XCircle className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  if (loading && !business) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !business || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4" style={{ color: textColor }}>Business Not Found</h1>
          <p className="mb-6" style={{ color: mutedColor }}>{error || 'The requested business could not be found.'}</p>
          <Link 
            to={`/${businessShortName}`}
            className="inline-block px-6 py-2 rounded-lg font-medium transition-opacity hover:opacity-90 text-white"
            style={{ backgroundColor: brandColor }}
          >
            Back to Business
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{
        backgroundColor: bgColor,
        fontFamily: branding?.fontFamily || 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        color: textColor
      }}
    >
      {/* Header */}
      <header 
        className="sticky top-0 z-50"
        style={{
          backgroundColor: surfaceColor,
          borderBottom: `1px solid ${borderColor}`
        }}
      >
        <nav className="flex items-center py-3">
          <div className="container mx-auto px-4 w-full">
            <div className="flex items-center justify-between w-full">
              <Link to={`/${businessShortName}`} className="flex items-center gap-2 min-w-0">
                {branding?.logoUrl ? (
                  <img 
                    src={branding.logoUrl} 
                    alt={`${business.name} Logo`}
                    className="w-12 h-12 rounded-2xl object-cover"
                  />
                ) : (
                  <div 
                    className="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-xl shadow-md"
                    style={{
                      background: 'conic-gradient(from 180deg at 50% 50%, #facc15, #fde047, #f97316, #facc15)',
                      color: '#92400e'
                    }}
                  >
                    {getBusinessInitials()}
                  </div>
                )}
                <div className="min-w-0">
                  <div 
                    className="font-semibold text-base whitespace-nowrap overflow-hidden text-ellipsis max-w-[260px]"
                    style={{ color: textColor }}
                  >
                    {business.name}
                  </div>
                  <div 
                    className="text-xs uppercase tracking-wider"
                    style={{ color: mutedColor }}
                  >
                    Powered by AACore
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </nav>
      </header>

      <main className="flex-1">
        <div className="container mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate(`/${businessShortName}`)}
              className="text-sm hover:opacity-80 transition-opacity mb-4 bg-transparent border-none cursor-pointer flex items-center"
              style={{ color: mutedColor }}
            >
              <span className="mr-1">←</span>
              <span>Back to {business.name}</span>
            </button>
            <h1 
              className="text-3xl font-bold mb-2"
              style={{ color: textColor }}
            >
              My Appointments
            </h1>
            <p 
              className="text-sm"
              style={{ color: mutedColor }}
            >
              View and manage your appointments with {business.name}
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="mb-6">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {(['all', 'upcoming', 'completed', 'cancelled'] as AppointmentFilter[]).map((filter) => {
                const isActive = appointmentFilter === filter;
                return (
                  <button
                    key={filter}
                    onClick={() => {
                      setAppointmentFilter(filter);
                      setCurrentPage(1);
                    }}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap"
                    style={{
                      backgroundColor: isActive ? brandColor : brandSoft,
                      color: isActive ? '#fff' : mutedColor,
                      borderColor: isActive ? brandColor : 'transparent',
                      boxShadow: isActive ? `0 8px 18px ${brandColor}59` : 'none'
                    }}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Appointments List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : appointments.length === 0 ? (
            <div 
              className="rounded-xl border p-8 text-center"
              style={{
                backgroundColor: surfaceColor,
                borderColor: borderColor
              }}
            >
              <Calendar className="w-12 h-12 mx-auto mb-4" style={{ color: mutedColor }} />
              <h3 
                className="text-lg font-semibold mb-2"
                style={{ color: textColor }}
              >
                No appointments found
              </h3>
              <p 
                className="text-sm mb-4"
                style={{ color: mutedColor }}
              >
                {appointmentFilter === 'all' 
                  ? "You don't have any appointments with this business yet."
                  : `You don't have any ${appointmentFilter} appointments.`}
              </p>
              <button
                onClick={() => navigate(`/${businessShortName}/booking`)}
                className="px-6 py-2 rounded-lg font-medium transition-opacity hover:opacity-90 text-white"
                style={{
                  backgroundColor: brandColor,
                  borderColor: brandColor,
                  boxShadow: `0 8px 18px ${brandColor}4d`
                }}
              >
                Book an Appointment
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {appointments.map((appointment) => {
                const statusColors = getStatusColor(appointment.status);
                const totalPrice = appointment.assignments?.reduce((sum, a) => sum + (a.price || 0), 0) || 0;
                const totalDuration = appointment.assignments?.reduce((sum, a) => sum + (a.durationMinutes || 0), 0) || 0;
                
                return (
                  <div
                    key={appointment.id}
                    className="rounded-xl border p-6 shadow-md hover:shadow-lg transition-shadow"
                    style={{
                      backgroundColor: surfaceColor,
                      borderColor: borderColor
                    }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div 
                            className={`p-2 rounded-lg ${statusColors.bg}`}
                            style={{ color: statusColors.text }}
                          >
                            {getStatusIcon(appointment.status)}
                          </div>
                          <div className="flex-1">
                            <h3 
                              className="font-semibold text-lg mb-1"
                              style={{ color: textColor }}
                            >
                              {appointment.assignments?.map(a => a.serviceName).join(', ') || 'Appointment'}
                            </h3>
                            <span 
                              className="px-2.5 py-1 text-xs font-medium rounded-full"
                              style={{
                                backgroundColor: statusColors.bg.replace('100', '200'),
                                color: statusColors.text
                              }}
                            >
                              {appointment.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-start gap-2">
                        <Calendar className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: brandColor }} />
                        <div>
                          <div 
                            className="text-xs uppercase tracking-wider mb-1"
                            style={{ color: mutedColor }}
                          >
                            Date & Time
                          </div>
                          <div style={{ color: textColor }}>
                            {getAppointmentStartIso(appointment) ? formatDate(getAppointmentStartIso(appointment)!) : 'Date TBD'}
                          </div>
                          <div 
                            className="text-sm"
                            style={{ color: mutedColor }}
                          >
                            {getAppointmentStartIso(appointment) ? formatTime(getAppointmentStartIso(appointment)!) : '—'}
                          </div>
                        </div>
                      </div>

                      {totalDuration > 0 && (
                        <div className="flex items-start gap-2">
                          <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: brandColor }} />
                          <div>
                            <div 
                              className="text-xs uppercase tracking-wider mb-1"
                              style={{ color: mutedColor }}
                            >
                              Duration
                            </div>
                            <div style={{ color: textColor }}>
                              {Math.floor(totalDuration / 60)}h {totalDuration % 60 > 0 ? `${totalDuration % 60}m` : ''}
                            </div>
                          </div>
                        </div>
                      )}

                      {totalPrice > 0 && (
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 flex-shrink-0 mt-0.5 flex items-center justify-center" style={{ color: brandColor }}>
                            $
                          </div>
                          <div>
                            <div 
                              className="text-xs uppercase tracking-wider mb-1"
                              style={{ color: mutedColor }}
                            >
                              Total Price
                            </div>
                            <div 
                              className="font-semibold"
                              style={{ color: textColor }}
                            >
                              {formatPrice(totalPrice)}
                            </div>
                          </div>
                        </div>
                      )}

                      {appointment.assignments?.some(a => a.professionalFirstName) && (
                        <div className="flex items-start gap-2">
                          <User className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: brandColor }} />
                          <div>
                            <div 
                              className="text-xs uppercase tracking-wider mb-1"
                              style={{ color: mutedColor }}
                            >
                              Professional
                            </div>
                            <div style={{ color: textColor }}>
                              {appointment.assignments
                                .filter(a => a.professionalFirstName && a.professionalLastName)
                                .map(a => `${a.professionalFirstName} ${a.professionalLastName!.charAt(0)}.`)
                                .join(', ')}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {appointment.notes && (
                      <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: brandSoft }}>
                        <div 
                          className="text-xs uppercase tracking-wider mb-1"
                          style={{ color: mutedColor }}
                        >
                          Notes
                        </div>
                        <p 
                          className="text-sm"
                          style={{ color: textColor }}
                        >
                          {appointment.notes}
                        </p>
                      </div>
                    )}

                    {appointment.cancellationNotes && (
                      <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: '#fee2e2', borderColor: '#fecaca' }}>
                        <div 
                          className="text-xs uppercase tracking-wider mb-1"
                          style={{ color: '#991b1b' }}
                        >
                          Cancellation Reason
                        </div>
                        <p 
                          className="text-sm"
                          style={{ color: '#7f1d1d' }}
                        >
                          {appointment.cancellationNotes}
                        </p>
                      </div>
                    )}

                    {(appointment.status === 'PENDING' || appointment.status === 'CONFIRMED') && (
                      <div className="flex gap-2 pt-4 border-t" style={{ borderColor: borderColor }}>
                        <button
                          onClick={() => navigate(`/dashboard/customer/reschedule/${appointment.id}?returnTo=/${businessShortName}/appointments`)}
                          className="flex-1 px-4 py-2 rounded-lg font-medium transition-opacity hover:opacity-80 border"
                          style={{
                            borderColor: `${brandColor}59`,
                            color: brandColor,
                            backgroundColor: 'transparent'
                          }}
                        >
                          Reschedule
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to cancel this appointment?')) {
                              try {
                                await apiService.cancelAppointment(appointment.id, {
                                  noShow: false,
                                  cancellationNotes: ''
                                });
                                toast.success('Appointment cancelled successfully');
                                // Reload appointments
                                const filters: any = {
                                  businessId: business.id,
                                  customerId: 'me',
                                  page: currentPage,
                                  limit: 10
                                };
                                if (appointmentFilter === 'upcoming') {
                                  filters.status = 'upcoming';
                                } else if (appointmentFilter === 'completed') {
                                  filters.status = 'COMPLETED';
                                } else if (appointmentFilter === 'cancelled') {
                                  filters.status = 'CANCELLED';
                                }
                                const response = await apiService.getCustomerAppointments(filters);
                                setAppointments(response.data || []);
                              } catch (error: any) {
                                console.error('Error cancelling appointment:', error);
                                toast.error(error.response?.data?.message || 'Failed to cancel appointment');
                              }
                            }
                          }}
                          className="flex-1 px-4 py-2 rounded-lg font-medium transition-opacity hover:opacity-80"
                          style={{
                            backgroundColor: '#dc2626',
                            color: '#fff'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-8">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={!pagination.hasPrevPage}
                className="px-4 py-2 rounded-lg font-medium transition-opacity disabled:opacity-50 disabled:cursor-not-allowed border"
                style={{
                  borderColor: pagination.hasPrevPage ? `${brandColor}59` : borderColor,
                  color: pagination.hasPrevPage ? brandColor : mutedColor,
                  backgroundColor: 'transparent'
                }}
              >
                Previous
              </button>
              <span 
                className="px-4 py-2 text-sm"
                style={{ color: mutedColor }}
              >
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                disabled={!pagination.hasNextPage}
                className="px-4 py-2 rounded-lg font-medium transition-opacity disabled:opacity-50 disabled:cursor-not-allowed border"
                style={{
                  borderColor: pagination.hasNextPage ? `${brandColor}59` : borderColor,
                  color: pagination.hasNextPage ? brandColor : mutedColor,
                  backgroundColor: 'transparent'
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer 
        className="mt-10 pt-4 pb-5"
        style={{
          backgroundColor: surfaceColor,
          borderTop: `1px solid ${borderColor}`
        }}
      >
        <div className="container mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-2">
          <div className="flex gap-2">
            <a
              href="#"
              className="w-8 h-8 rounded-full border inline-flex items-center justify-center"
              style={{
                borderColor: '#e5e7eb',
                backgroundColor: '#f9fafb',
                color: textColor
              }}
              aria-label="Instagram"
            >
              <i className="bi bi-instagram"></i>
            </a>
            <a
              href="#"
              className="w-8 h-8 rounded-full border inline-flex items-center justify-center"
              style={{
                borderColor: '#e5e7eb',
                backgroundColor: '#f9fafb',
                color: textColor
              }}
              aria-label="Facebook"
            >
              <i className="bi bi-facebook"></i>
            </a>
            <a
              href="#"
              className="w-8 h-8 rounded-full border inline-flex items-center justify-center"
              style={{
                borderColor: '#e5e7eb',
                backgroundColor: '#f9fafb',
                color: textColor
              }}
              aria-label="YouTube"
            >
              <i className="bi bi-youtube"></i>
            </a>
          </div>
          <div className="text-end text-xs" style={{ color: mutedColor }}>
            <div>© {new Date().getFullYear()} {business.name}. All rights reserved.</div>
            <div>Powered by AACore · A&A Inomatic, LLC.</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BusinessAppointmentsPage;

